import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { SyncStatusIndicator } from '../components/SyncStatusIndicator'
import { EmptyState } from '../components/EmptyState'
import { useCommand } from '../hooks/useCommand'
import { useScenarioItems } from '../hooks/useScenarioItems'
import { useScenario } from '../hooks/useScenario'
import { useFlows } from '../hooks/useFlows'
import './ListBuilder.css'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { PageHeader } from '../components/PageHeader'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'
import { ListKindIcon } from '../components/PageKindIcons'
import { suggestScenarioDatedName } from '../utils/suggestedNames'
import { ChevronDownIcon, MinusIcon, PencilIcon, PlusIcon, TrashIcon } from '../components/Icons'
import { InlineRenameInput } from './ListItem'
import { ItemFlowPreferences } from '../components/ItemFlowPreferences'
import type { FlowPreferenceStatus } from '../../core/domain/models'
import { resolveFlowPreferenceStatus } from '../../core/domain/flowPreferenceStatus'
import { toastStore } from '../notifications/toast-store'

type QuantityScope = 'apply-once' | 'remember'

type QuantityOverride = {
    quantity: number
    scope: QuantityScope
}

export default function ListBuilder() {
    const { scenarioId, listId } = useParams<{ scenarioId: string; listId?: string }>()
    const navigate = useNavigate()
    const { createList, addItems, updateItemQuantity, renameItem, deleteItem, deleteScenarioItems, setItemFlowPreferences } = useCommand()
    
    // Check architecture: UI -> Hook -> Domain
    const { items, loading: itemsLoading } = useScenarioItems(scenarioId, { passive: true })
    const { scenario } = useScenario(scenarioId || '', { passive: true })
    const { flows } = useFlows(scenarioId || '')
    
    const loading = itemsLoading || (!!listId && !scenario)

    const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set())
    const [listName, setListName] = useState('')
    const [existingItemIds, setExistingItemIds] = useState<Set<string>>(new Set())
    const [isCreating, setIsCreating] = useState(false)
    const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'name'>('popular')

    const [expandedItemId, setExpandedItemId] = useState<string | null>(null)
    const [renamingItemId, setRenamingItemId] = useState<string | null>(null)
    const [quantityDrafts, setQuantityDrafts] = useState<Record<string, number>>({})
    const [quantityOverrides, setQuantityOverrides] = useState<Record<string, QuantityOverride>>({})

    const hasInitializedEditMode = useRef(false)

    const isEditMode = !!listId

    // Build a map from item name to ID for looking up existing items
    const itemsByName = useMemo(() => new Map(items.map(item => [item.name.toLowerCase(), item])), [items])

    const MAX_QUANTITY = 999

    const getBaseQuantity = (item: { quantity?: number }) => {
        const q = typeof item.quantity === 'number' && Number.isFinite(item.quantity) ? Math.floor(item.quantity) : 1
        return Math.max(1, Math.min(MAX_QUANTITY, q))
    }

    const getEffectiveQuantity = (item: { id: string; quantity?: number }) => {
        const override = quantityOverrides[item.id]
        if (override) return Math.max(1, Math.min(MAX_QUANTITY, Math.floor(override.quantity)))
        return getBaseQuantity(item)
    }

    const getDraftQuantity = (item: { id: string; quantity?: number }) => {
        const draft = quantityDrafts[item.id]
        if (typeof draft === 'number' && Number.isFinite(draft)) {
            return Math.max(1, Math.min(MAX_QUANTITY, Math.floor(draft)))
        }
        return getEffectiveQuantity(item)
    }

    const updateDraftQuantity = (itemId: string, quantity: number) => {
        const next = Math.max(1, Math.min(MAX_QUANTITY, Math.floor(quantity)))
        setQuantityDrafts((prev) => ({
            ...prev,
            [itemId]: next
        }))
    }

    const applyQuantityOverride = (itemId: string, scope: QuantityScope) => {
        const item = items.find(i => i.id === itemId)
        if (!item) return

        const nextQuantity = getDraftQuantity(item)
        setQuantityOverrides((prev) => ({
            ...prev,
            [itemId]: {
                quantity: nextQuantity,
                scope
            }
        }))
    }

    useEffect(() => {
        if (isEditMode) return
        if (!scenario) return

        setListName((current) => {
            if (current.trim()) return current
            return suggestScenarioDatedName(scenario.name)
        })
    }, [isEditMode, scenario])

    useEffect(() => {
        if (!isEditMode || !scenario || hasInitializedEditMode.current) return
        // Wait for scenario items to be loaded before initializing
        if (itemsByName.size === 0) return

        const list = scenario.lists.find(l => l.id === listId)
        if (list) {
            // Map existing list item names to scenario item IDs
            const existingIds = Object.values(list.items ?? {})
                .map(item => itemsByName.get(item.name.toLowerCase())?.id)
                .filter((id): id is string => !!id)
            setExistingItemIds(new Set(existingIds))
            setSelectedItemIds(new Set(existingIds))
            setListName(list.name)
            hasInitializedEditMode.current = true
        }
    }, [isEditMode, scenario, listId, itemsByName])

    const toggleSelection = (itemId: string) => {
        const newSelected = new Set(selectedItemIds)
        if (newSelected.has(itemId)) {
            newSelected.delete(itemId)
        } else {
            newSelected.add(itemId)
        }
        setSelectedItemIds(newSelected)
    }

    const toggleExpand = (itemId: string) => {
        setExpandedItemId((prev) => (prev === itemId ? null : itemId))
    }

    const handleStartRename = (itemId: string) => {
        setRenamingItemId(itemId)
    }

    const handleSubmitRename = async (itemId: string, newName: string, oldName: string) => {
        setRenamingItemId(null)
        if (newName === oldName || !scenarioId) return

        // Determine which list contains this item.
        // In edit mode, prefer the current list (listId from URL).
        // Fall back to searching scenario lists for items that belong to a different list.
        const targetListId = scenario?.lists.find(l => l.items && itemId in l.items)?.id ?? listId
        if (!targetListId) return

        try {
            await renameItem(scenarioId, targetListId, itemId, newName, oldName)
        } catch (error) {
            console.error('Error renaming item:', error)
        }
    }

    const handleDeleteItem = async (itemId: string, itemName: string) => {
        if (!scenarioId) return

        try {
            if (listId) {
                await deleteItem(scenarioId, listId, itemId, itemName)
            } else {
                await deleteScenarioItems(scenarioId, [itemId])
            }
            // Remove from existingItemIds since it's no longer in the list
            setExistingItemIds(prev => {
                const next = new Set(prev)
                next.delete(itemId)
                return next
            })
            // Also deselect it
            setSelectedItemIds(prev => {
                const next = new Set(prev)
                next.delete(itemId)
                return next
            })
        } catch (error) {
            console.error('Error deleting item:', error)
        }
        setExpandedItemId(null)
    }

    const handleFlowPreferencesCommit = useCallback(async (itemId: string, changes: Record<string, FlowPreferenceStatus>) => {
        if (!scenarioId) return

        const flowPreferences: Record<string, { status: 'normal' | 'preferred' | 'notAvailable' }> = {}
        for (const [flowId, status] of Object.entries(changes)) {
            flowPreferences[flowId] = { status }
        }

        if (Object.keys(flowPreferences).length === 0) return

        // Capture previous status for undo
        const previousPreferences: Record<string, { status: 'normal' | 'preferred' | 'notAvailable' }> = {}
        for (const flowId of Object.keys(flowPreferences)) {
            const currentFlow = flows.find(f => f.id === flowId)
            const previousStatus = resolveFlowPreferenceStatus(currentFlow?.rankings?.[itemId])
            previousPreferences[flowId] = { status: previousStatus }
        }

        const itemName = items.find(i => i.id === itemId)?.name ?? 'Item'
        const flowNames = Object.keys(flowPreferences)
            .map(fid => flows.find(f => f.id === fid)?.name)
            .filter(Boolean)

        toastStore.push({
            message: `Updated preferences${flowNames.length === 1 ? ` for ${flowNames[0]}` : ''}`,
            itemName,
            type: 'success',
            duration: 5000,
            action: {
                label: 'Undo',
                onClick: () => {
                    setItemFlowPreferences(scenarioId, itemId, previousPreferences, { skipNotification: true })
                },
            },
        })

        try {
            await setItemFlowPreferences(scenarioId, itemId, flowPreferences, { skipNotification: true })
        } catch (error) {
            console.error('Error setting item flow preferences:', error)
        }
    }, [scenarioId, setItemFlowPreferences, flows, items])

    const toggleSelectAll = () => {
        if (selectedItemIds.size === items.length) {
            setSelectedItemIds(new Set())
        } else {
            setSelectedItemIds(new Set(items.map(s => s.id)))
        }
    }

    const handleCreate = async () => {
        if (!scenarioId || !listName.trim() || selectedItemIds.size === 0) return
        
        setIsCreating(true)
        try {
            // Generate a new list ID
            const newListId = crypto.randomUUID()
            
            // Create the list
            await createList(scenarioId, newListId, listName.trim())
            
            // Add all selected items to the new list using batch command
            // Reuse scenario item IDs for tracking across lists
            const selectedItems = items.filter(item => selectedItemIds.has(item.id))
            await addItems(
                scenarioId,
                newListId,
                selectedItems.map(item => {
                    const override = quantityOverrides[item.id]
                    // Only "Remember" should update scenario item quantity, and it should only happen on create/add.
                    // We achieve that by only sending a quantity in CreateItems when scope is remember.
                    return {
                        id: item.id,
                        name: item.name,
                        quantity: override?.scope === 'remember' ? override.quantity : undefined
                    }
                })
            )

            // Apply "Apply once" overrides after items exist, without touching scenario item quantity.
            const applyOnceOverrides = selectedItems
                .map(item => ({ item, override: quantityOverrides[item.id] }))
                .filter((x): x is { item: typeof selectedItems[number]; override: QuantityOverride } => !!x.override && x.override.scope === 'apply-once')
                .sort((a, b) => a.item.id.localeCompare(b.item.id))

            for (const { item, override } of applyOnceOverrides) {
                const baseQuantity = getBaseQuantity(item)
                const desired = Math.max(1, Math.min(MAX_QUANTITY, Math.floor(override.quantity)))
                if (desired === baseQuantity) continue
                await updateItemQuantity(scenarioId, newListId, item.id, desired, false)
            }
            
            // Navigate to the newly created list.
            // Use `replace` so browser Back returns to the scenario (not this builder).
            navigate(`/scenario/${scenarioId}/list/${newListId}`, { replace: true })
        } catch (error) {
            console.error('Failed to create list:', error)
            alert('Failed to create list. Please try again.')
        } finally {
            setIsCreating(false)
        }
    }

    const handleAddItems = async () => {
        if (!scenarioId || !listId || selectedItemIds.size === 0) return
        
        // Find newly selected items (not in existingItems)
        const newItemIds = Array.from(selectedItemIds).filter(id => !existingItemIds.has(id))
        
        if (newItemIds.length === 0) {
            navigate(`/scenario/${scenarioId}/list/${listId}`, { replace: true })
            return
        }
        
        setIsCreating(true)
        try {
            // Reuse scenario item IDs for tracking across lists
            const newItems = items.filter(item => newItemIds.includes(item.id))

            await addItems(
                scenarioId,
                listId,
                newItems.map(item => {
                    const override = quantityOverrides[item.id]
                    return {
                        id: item.id,
                        name: item.name,
                        quantity: override?.scope === 'remember' ? override.quantity : undefined
                    }
                })
            )

            const applyOnceOverrides = newItems
                .map(item => ({ item, override: quantityOverrides[item.id] }))
                .filter((x): x is { item: typeof newItems[number]; override: QuantityOverride } => !!x.override && x.override.scope === 'apply-once')
                .sort((a, b) => a.item.id.localeCompare(b.item.id))

            for (const { item, override } of applyOnceOverrides) {
                const baseQuantity = getBaseQuantity(item)
                const desired = Math.max(1, Math.min(MAX_QUANTITY, Math.floor(override.quantity)))
                if (desired === baseQuantity) continue
                await updateItemQuantity(scenarioId, listId, item.id, desired, false)
            }

            // This is a transient flow ("add-items"). Replace it so browser Back doesn't reopen it.
            navigate(`/scenario/${scenarioId}/list/${listId}`, { replace: true })
        } catch (error) {
            console.error('Failed to add items:', error)
            alert('Failed to add items. Please try again.')
        } finally {
            setIsCreating(false)
        }
    }

    const handleCancel = () => {
        navigate(-1)
    }

    const newlySelectedCount = isEditMode 
        ? Array.from(selectedItemIds).filter(id => !existingItemIds.has(id)).length
        : selectedItemIds.size

    // Sort items based on selected criteria
    const sortedItems = [...items].sort((a, b) => {
        switch (sortBy) {
            case 'recent':
                return new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime()
            case 'popular':
                return b.usageCount - a.usageCount
            case 'name':
                return a.name.localeCompare(b.name)
            default:
                return 0
        }
    })

    if (loading) {
        return (
            <div className="list-builder-page">
                <LoadingSpinner message="Loading items..." />
            </div>
        )
    }

    return (
        <div className="app-container">
            <PageHeader
                kind="List"
                kindIcon={<ListKindIcon />}
                tone="list"
                title="Build List"
                backLabel="Scenario"
                onBack={handleCancel}
                actions={<SyncStatusIndicator />}
            />

            <PwaUpdateBanner />

            <div className="list-builder-page">
                <div className="list-builder-content">
                    {!isEditMode && (
                        <div className="list-name-section">
                            <input
                                type="text"
                                className="list-name-input"
                                placeholder="List name..."
                                value={listName}
                                onChange={(e) => setListName(e.target.value)}
                                autoFocus
                            />
                        </div>
                    )}

                    <div className="selection-toolbar">
                <div className="selection-info">
                    <input
                        type="checkbox"
                        checked={selectedItemIds.size === items.length}
                        onChange={toggleSelectAll}
                        id="select-all"
                    />
                    <label htmlFor="select-all">
                        {selectedItemIds.size === items.length ? 'Deselect All' : 'Select All'}
                    </label>
                    <span className="count">
                        {selectedItemIds.size} of {items.length} selected
                    </span>
                </div>
                <div className="sort-controls">
                    <label htmlFor="sort-by">Sort by:</label>
                    <select 
                        id="sort-by" 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value as 'recent' | 'popular' | 'name')}
                        className="sort-select"
                    >
                        <option value="popular">Most Popular</option>
                        <option value="recent">Most Recent</option>
                        <option value="name">Name</option>
                    </select>
                </div>
            </div>

            <div className="items-list">
                {items.length === 0 ? (
                    <EmptyState
                        icon="📝"
                        title="No Items Found"
                        description="Add some items to your lists first! As you use the app, your frequently used items will appear here."
                    />
                ) : (
                    <div className="items-grid">
                        {sortedItems.map((item) => {
                            const isSelected = selectedItemIds.has(item.id)
                            const isExisting = existingItemIds.has(item.id)
                            const isExpanded = expandedItemId === item.id

                            const effectiveQuantity = getEffectiveQuantity(item)
                            const draftQuantity = getDraftQuantity(item)
                            const isQuantityDirty = draftQuantity !== effectiveQuantity
                            
                            return (
                                <div key={item.id}>
                                    <div
                                        className={`item-card ${isSelected ? 'selected' : ''} ${isExisting ? 'existing' : ''} ${isExpanded ? 'expanded' : ''}`}
                                        onClick={() => renamingItemId !== item.id && toggleSelection(item.id)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {}}
                                            className="item-checkbox"
                                        />
                                        <span className="item-name">
                                            {renamingItemId === item.id ? (
                                                <InlineRenameInput
                                                    currentName={item.name}
                                                    onSubmit={(newName) => handleSubmitRename(item.id, newName, item.name)}
                                                    onCancel={() => setRenamingItemId(null)}
                                                />
                                            ) : (
                                                <>{effectiveQuantity > 1 ? `${effectiveQuantity}x ${item.name}` : item.name}</>
                                            )}
                                        </span>
                                        {isExisting && <span className="existing-badge">In list</span>}
                                        <button
                                            type="button"
                                            className="item-expand-btn"
                                            aria-label={isExpanded ? 'Hide options' : 'Show options'}
                                            title={isExpanded ? 'Hide options' : 'Show options'}
                                            aria-expanded={isExpanded}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                toggleExpand(item.id)
                                                // Ensure the draft starts from the currently applied value when opening.
                                                if (!isExpanded) {
                                                    updateDraftQuantity(item.id, getEffectiveQuantity(item))
                                                }
                                            }}
                                        >
                                            <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`} aria-hidden="true">
                                                <ChevronDownIcon />
                                            </span>
                                        </button>
                                    </div>

                                    <div
                                        className={`item-options-panel ${isExpanded ? 'is-open' : 'is-closed'}`}
                                        aria-hidden={!isExpanded}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="item-options-panel-inner">
                                            <div className="item-options-left">
                                                {flows.length > 0 && (
                                                    <ItemFlowPreferences
                                                        itemId={item.id}
                                                        flows={flows}
                                                        onCommit={handleFlowPreferencesCommit}
                                                        commitMode="on-collapse"
                                                        isOpen={isExpanded}
                                                    />
                                                )}
                                                <div className="item-action-buttons">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleStartRename(item.id)
                                                            setExpandedItemId(null)
                                                        }}
                                                        className="item-option-btn rename"
                                                        title="Rename"
                                                        aria-label="Rename"
                                                    >
                                                        <PencilIcon />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteItem(item.id, item.name)}
                                                        className="item-option-btn delete"
                                                        title="Delete"
                                                        aria-label="Delete"
                                                    >
                                                        <TrashIcon />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="item-options-right">
                                                <div className="item-quantity-row">
                                                    <span className="item-quantity-label">Qty:</span>
                                                    <div className="item-quantity-stepper">
                                                        <button
                                                            type="button"
                                                            className="item-quantity-btn"
                                                            aria-label="Decrease quantity"
                                                            title="Decrease quantity"
                                                            disabled={draftQuantity <= 1}
                                                            onClick={() => updateDraftQuantity(item.id, Math.max(1, draftQuantity - 1))}
                                                        >
                                                            <MinusIcon />
                                                        </button>

                                                        <input
                                                            className="item-quantity-input"
                                                            aria-label="Quantity input"
                                                            title="Quantity"
                                                            type="number"
                                                            min={1}
                                                            value={draftQuantity}
                                                            onChange={(e) => {
                                                                const raw = e.target.value
                                                                const next = raw === '' ? 1 : Number(raw)
                                                                if (!Number.isFinite(next)) return
                                                                updateDraftQuantity(item.id, next)
                                                            }}
                                                        />

                                                        <button
                                                            type="button"
                                                            className="item-quantity-btn"
                                                            aria-label="Increase quantity"
                                                            title="Increase quantity"
                                                            disabled={draftQuantity >= MAX_QUANTITY}
                                                            onClick={() => updateDraftQuantity(item.id, Math.min(MAX_QUANTITY, draftQuantity + 1))}
                                                        >
                                                            <PlusIcon />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="item-quantity-scope">
                                                    <button
                                                        type="button"
                                                        className="item-quantity-scope-btn"
                                                        aria-label="Apply once"
                                                        title="Change quantity for this list only"
                                                        disabled={!isQuantityDirty}
                                                        onClick={() => {
                                                            applyQuantityOverride(item.id, 'apply-once')
                                                            setExpandedItemId(null)
                                                        }}
                                                    >
                                                        Apply once
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="item-quantity-scope-btn scope-btn-remember"
                                                        aria-label="Remember quantity"
                                                        title="Change quantity and remember for next time"
                                                        disabled={!isQuantityDirty}
                                                        onClick={() => {
                                                            applyQuantityOverride(item.id, 'remember')
                                                            setExpandedItemId(null)
                                                        }}
                                                    >
                                                        Remember
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
                </div>
            </div>
            </div>

            <div className="action-bar">
                <div className="action-bar-content">
                    <button onClick={handleCancel} className="cancel-btn" disabled={isCreating}>
                        Cancel
                    </button>
                    {isEditMode ? (
                        <button
                            onClick={handleAddItems}
                            className="submit-btn"
                            disabled={isCreating || newlySelectedCount === 0}
                        >
                            {isCreating ? 'Adding...' : `Add ${newlySelectedCount} Item${newlySelectedCount !== 1 ? 's' : ''}`}
                        </button>
                    ) : (
                        <button
                            onClick={handleCreate}
                            className="submit-btn"
                            disabled={isCreating || !listName.trim() || selectedItemIds.size === 0}
                        >
                            {isCreating ? 'Creating...' : `Create List (${selectedItemIds.size})`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
